import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import {
    ScheduleApplicationStatus,
    ScheduleLifecycleVerb,
} from "@common/types/domains/accounting/schedule";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { canMarkMilestoneAchieved } from "../milestoneRowHelpers";
import {
    getScheduleSourceMenuItem,
    ScheduleApplicationRow,
} from "../scheduleApplicationRowHelpers";
import { getScheduleRowApplicationId } from "../scheduleApplicationRowId";
import { useScheduleApplicationsTableMeta } from "../scheduleApplicationsTableMeta";
import { getAvailableScheduleLifecycleVerbs } from "../scheduleApplicationStatusDisplay";
import {
    MILESTONE_ACHIEVED_MENU_LABEL,
    SCHEDULE_LIFECYCLE_MENU_LABELS,
} from "../scheduleLifecycleCopy";
import {
    SCHEDULE_MARK_BILLED_LABEL,
    SCHEDULE_MARK_UNBILLED_LABEL,
} from "../scheduleBillingCopy";
import {
    canRowMarkBilled,
    canRowMarkUnbilled,
    hasRowNothingToBillToday,
    toScheduleBillingTarget,
} from "../scheduleBillingRowHelpers";
import { ScheduleBillingAction } from "../useScheduleBillingActions";

const DESTRUCTIVE_VERBS: ReadonlySet<ScheduleLifecycleVerb> = new Set([
    ScheduleLifecycleVerb.delete,
]);

export const ScheduleApplicationActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ScheduleApplicationRow, string>) => {
        const meta = useScheduleApplicationsTableMeta(table);
        const rowData = row.original;
        const onRequestLifecycleAction =
            meta.callbacks?.onRequestLifecycleAction;
        const onMilestoneAchieved = meta.callbacks?.onMilestoneAchieved;
        if (rowData.rowType === "period") {
            if (!onMilestoneAchieved || !canMarkMilestoneAchieved(rowData)) {
                return null;
            }
            return (
                <ViewTableCell>
                    <CaptureClicks>
                        <Menu.More
                            className={viewTableStyles.showOnHover}
                            aria-label="Milestone actions"
                        >
                            <Menu.Item
                                onClick={() => onMilestoneAchieved(rowData)}
                            >
                                {MILESTONE_ACHIEVED_MENU_LABEL}
                            </Menu.Item>
                        </Menu.More>
                    </CaptureClicks>
                </ViewTableCell>
            );
        }
        if (rowData.rowType !== "application") {
            return null;
        }
        const applicationId = getScheduleRowApplicationId(rowData);
        const sourceMenuItem = getScheduleSourceMenuItem(rowData);
        const verbs = onRequestLifecycleAction
            ? getAvailableScheduleLifecycleVerbs(rowData)
            : [];
        const onRequestBillingAction = meta.callbacks?.onRequestBillingAction;
        const billingActions = onRequestBillingAction
            ? [
                  ...(canRowMarkBilled(rowData) &&
                  !hasRowNothingToBillToday(rowData)
                      ? [
                            {
                                action: ScheduleBillingAction.markBilled,
                                label: SCHEDULE_MARK_BILLED_LABEL,
                            },
                        ]
                      : []),
                  ...(canRowMarkUnbilled(rowData)
                      ? [
                            {
                                action: ScheduleBillingAction.markUnbilled,
                                label: SCHEDULE_MARK_UNBILLED_LABEL,
                            },
                        ]
                      : []),
              ]
            : [];
        const onOpenInReports = meta.callbacks?.onOpenInReports;
        if (
            !sourceMenuItem &&
            verbs.length === 0 &&
            billingActions.length === 0 &&
            !onOpenInReports
        ) {
            return null;
        }
        const openSource = () => {
            if (!sourceMenuItem) {
                return;
            }
            if (sourceMenuItem.action === "transaction") {
                meta.callbacks?.onOpenTransaction({
                    transactionId: sourceMenuItem.transactionId,
                    entityId: rowData.entityId,
                });
                return;
            }
            if (sourceMenuItem.action === "invoice") {
                meta.callbacks?.onOpenInvoice({
                    invoiceId: sourceMenuItem.invoiceId,
                    groupBy: sourceMenuItem.groupBy,
                });
                return;
            }
            if (sourceMenuItem.action === "bill") {
                meta.callbacks?.onOpenBill(sourceMenuItem.billId);
                return;
            }
            onRequestLifecycleAction?.({
                applicationId,
                entityId: rowData.entityId,
                sourceType: rowData.sourceType,
                amount: rowData.amount,
                verb: ScheduleLifecycleVerb.change,
            });
        };
        return (
            <ViewTableCell>
                <CaptureClicks>
                    <Menu.More className={viewTableStyles.showOnHover}>
                        {sourceMenuItem ? (
                            <Menu.Item onClick={openSource}>
                                {sourceMenuItem.label}
                            </Menu.Item>
                        ) : null}
                        {billingActions.map(({ action, label }) => (
                            <Menu.Item
                                key={action}
                                onClick={() =>
                                    onRequestBillingAction?.({
                                        action,
                                        target: toScheduleBillingTarget(
                                            rowData,
                                        ),
                                    })
                                }
                            >
                                {label}
                            </Menu.Item>
                        ))}
                        {onOpenInReports &&
                        rowData.status !== ScheduleApplicationStatus.draft ? (
                            <Menu.Item onClick={() => onOpenInReports(rowData)}>
                                Go to reports
                            </Menu.Item>
                        ) : null}
                        {verbs.map((verb) => (
                            <Menu.Item
                                key={verb}
                                destructive={DESTRUCTIVE_VERBS.has(verb)}
                                onClick={() =>
                                    onRequestLifecycleAction?.({
                                        applicationId,
                                        entityId: rowData.entityId,
                                        sourceType: rowData.sourceType,
                                        amount: rowData.amount,
                                        verb,
                                    })
                                }
                            >
                                {SCHEDULE_LIFECYCLE_MENU_LABELS[verb]}
                            </Menu.Item>
                        ))}
                    </Menu.More>
                </CaptureClicks>
            </ViewTableCell>
        );
    },
);
ScheduleApplicationActionsCell.displayName = "ScheduleApplicationActionsCell";
